/**
 * Support Call Examples - XML-structured borderline calibration
 * Phase 2.2: Examples for support quality scoring
 */

export const SUPPORT_CALL_EXAMPLES = `
<examples category="support-call">

  <!-- Empathy: Developing (60) vs Proficient (80) -->

  <example category="empathy" score="60" tier="developing">
    <dialogue>
      <customer speaker="customer" timestamp="01:15">I've been trying to fix this for 3 hours!</customer>
      <agent speaker="agent" timestamp="01:18">
        OK, let me look into that. What's your account number?
      </agent>
    </dialogue>
    <analysis>
      <component name="acknowledgment">1 (acknowledged issue)</component>
      <component name="validation">0 (no validation of frustration)</component>
      <component name="personalization">0 (jumped to technical)</component>
      <component name="tone">0 (mechanical, not warm)</component>
      <reasoning>The issue is acknowledged, but there is no validation, no personalization and a mechanical tone: developing, 60.</reasoning>
    </analysis>
    <improvement>
      Missing emotional validation before technical response.
      Better: "I completely understand your frustration after 3 hours. Let's get this fixed right now. May I have your account number?"
    </improvement>
  </example>

  <example category="empathy" score="80" tier="proficient">
    <dialogue>
      <customer speaker="customer" timestamp="01:15">I've been trying to fix this for 3 hours!</customer>
      <agent speaker="agent" timestamp="01:18">
        I completely understand your frustration, Sarah. 3 hours is way too long. Let's get this fixed for you right now. May I have your account number so I can pull up your details?
      </agent>
    </dialogue>
    <analysis>
      <component name="acknowledgment">1 (acknowledged issue)</component>
      <component name="validation">1 (validated frustration)</component>
      <component name="personalization">1 (used customer name)</component>
      <component name="tone">1 (warm, helpful)</component>
      <reasoning>All four components are present and delivered naturally: proficient, 80.</reasoning>
    </analysis>
    <threshold_note>
      Score 70+ requires all 4 components: acknowledge, validate, personalize, warm tone.
      This demonstrates professional empathy.
    </threshold_note>
  </example>

  <borderline_calibration score_A="60" score_B="70">
    <question>What separates Developing empathy (60) from Proficient (70)?</question>
    <answer>
      - 70+ includes emotional validation ("I understand your frustration")
      - 60 acknowledges issue but skips emotional aspect
      - 70+ uses customer name, 60 is generic
      - 70+ shows urgency ("right now"), 60 is procedural
      - 70+ balances empathy + action, 60 jumps straight to technical
    </answer>
  </borderline_calibration>

  <!-- Problem Identification: Developing (55) vs Proficient (75) -->

  <example category="problem_identification" score="55" tier="developing">
    <dialogue>
      <customer speaker="customer" timestamp="02:10">The system won't let me log in</customer>
      <agent speaker="agent" timestamp="02:15">
        Let me reset your password. Check your email in a few minutes.
      </agent>
    </dialogue>
    <analysis>
      <component name="symptom_gathered">1 ("won't let me log in")</component>
      <component name="root_cause_explored">0 (no questions asked)</component>
      <component name="error_messages_checked">0 (didn't ask)</component>
      <component name="steps_to_reproduce">0 (assumed password issue)</component>
      <reasoning>The agent acts quickly, but acts on an assumption with no diagnosis: developing, 55.</reasoning>
    </analysis>
    <improvement>
      Assumed password issue without diagnosis.
      Better: "What message do you see when you try to log in?" → diagnose → fix root cause
    </improvement>
  </example>

  <example category="problem_identification" score="75" tier="proficient">
    <dialogue>
      <customer speaker="customer" timestamp="02:10">The system won't let me log in</customer>
      <agent speaker="agent" timestamp="02:15">
        I'll help you with that. What happens when you try to log in? Do you see an error message?
      </agent>
      <customer speaker="customer" timestamp="02:20">It says "invalid credentials"</customer>
      <agent speaker="agent" timestamp="02:25">
        OK, and you're certain the password is correct? Sometimes Caps Lock causes this.
      </agent>
      <customer speaker="customer" timestamp="02:30">Oh! Caps Lock was on. It works now!</customer>
    </dialogue>
    <analysis>
      <component name="symptom_gathered">1 (login failure)</component>
      <component name="root_cause_explored">1 (asked for error message)</component>
      <component name="error_messages_checked">1 (saw "invalid credentials")</component>
      <component name="steps_to_reproduce">1 (checked Caps Lock)</component>
      <reasoning>A complete diagnostic path, but the issue was simple, so it is proficient rather than exceptional: 75.</reasoning>
    </analysis>
    <threshold_note>
      Score 70+ requires diagnostic process before solution.
      Ask questions → understand root cause → fix correctly.
    </threshold_note>
  </example>

  <borderline_calibration score_A="55" score_B="75">
    <question>What separates Developing diagnosis (55) from Proficient (75)?</question>
    <answer>
      - 75+ asks diagnostic questions before proposing solution
      - 55 assumes cause and jumps to fix
      - 75+ gathers specific error messages/symptoms
      - 55 uses generic approach ("reset password" for everything)
      - 75+ confirms root cause before action, 55 hopes fix works
    </answer>
  </borderline_calibration>

  <!-- Resolution Quality: Developing (65) vs Proficient (85) -->

  <example category="resolution_quality" score="65" tier="developing">
    <dialogue>
      <agent speaker="agent" timestamp="05:40">
        I've reset your password. Try logging in now.
      </agent>
      <customer speaker="customer" timestamp="05:45">[tries] Yes, it works!</customer>
      <agent speaker="agent" timestamp="05:48">Great! Anything else?</agent>
    </dialogue>
    <analysis>
      <component name="problem_solved">1 (issue fixed)</component>
      <component name="solution_verified">1 (customer confirmed)</component>
      <component name="prevention_advised">0 (no guidance given)</component>
      <component name="follow_up_offered">0 (no proactive follow-up)</component>
      <reasoning>The problem is solved and confirmed by the customer, but there is no prevention advice or follow-up: developing, 65.</reasoning>
    </analysis>
    <improvement>
      Missing prevention advice and follow-up.
      Better: Add "To prevent this, please use our password manager. If you have any issues in the next 24h, call us back anytime."
    </improvement>
  </example>

  <example category="resolution_quality" score="85" tier="proficient">
    <dialogue>
      <agent speaker="agent" timestamp="05:40">
        I've reset your password. Please check your email and try logging in now.
      </agent>
      <customer speaker="customer" timestamp="05:45">[tries] Yes, it works!</customer>
      <agent speaker="agent" timestamp="05:48">
        Perfect! To prevent this in the future, I recommend using our password manager or saving it in your browser. If you experience any issues in the next 24 hours, please don't hesitate to call us back. Is there anything else I can help with today?
      </agent>
    </dialogue>
    <analysis>
      <component name="problem_solved">1 (issue fixed)</component>
      <component name="solution_verified">1 (customer confirmed)</component>
      <component name="prevention_advised">1 (password manager recommended)</component>
      <component name="follow_up_offered">1 (24h callback offer)</component>
      <reasoning>The full resolution cycle is present: fix, verification, prevention, follow-up: proficient, 85.</reasoning>
    </analysis>
    <threshold_note>
      Score 70+ requires complete resolution cycle:
      fix → verify → prevent → offer follow-up
    </threshold_note>
  </example>

  <borderline_calibration score_A="65" score_B="85">
    <question>What separates Developing resolution (65) from Proficient (85)?</question>
    <answer>
      - 85+ includes prevention advice (how to avoid in future)
      - 65 fixes problem but doesn't educate customer
      - 85+ offers proactive follow-up window ("if issues arise...")
      - 65 asks generic "anything else?", 85 is specific
      - 85+ closes loop completely, 65 stops at basic fix
    </answer>
  </borderline_calibration>

  <!-- First Call Resolution: Developing (50) vs Proficient (100) -->

  <example category="first_call_resolution" score="50" tier="developing">
    <dialogue>
      <agent speaker="agent" timestamp="06:30">
        I'll need to escalate this to our technical team. They'll call you back within 24 hours.
      </agent>
      <customer speaker="customer" timestamp="06:35">OK, I guess...</customer>
    </dialogue>
    <analysis>
      <component name="resolved_on_call">0 (escalated)</component>
      <component name="customer_satisfied">0 (reluctant acceptance)</component>
      <reasoning>
        Escalation necessary but customer not satisfied.
        No attempt to troubleshoot before escalating.
        The agent did set a clear expectation (callback within 24 hours), which keeps the score at 50 rather than lower.
      </reasoning>
    </analysis>
    <improvement>
      Try basic troubleshooting before escalating:
      "Let me try a few quick things first before escalating..."
    </improvement>
  </example>

  <example category="first_call_resolution" score="100" tier="exceptional">
    <dialogue>
      <agent speaker="agent" timestamp="06:30">
        I've fixed the issue and tested it on my end. Can you try now?
      </agent>
      <customer speaker="customer" timestamp="06:35">[tries] Perfect! Thank you so much!</customer>
      <agent speaker="agent" timestamp="06:40">
        You're welcome! I've documented this for our records. If it happens again, reference ticket #12345.
      </agent>
    </dialogue>
    <analysis>
      <component name="resolved_on_call">1 (fixed immediately)</component>
      <component name="customer_satisfied">1 (enthusiastic thanks)</component>
      <reasoning>
        Complete resolution on first call.
        Customer delighted, issue documented.
      </reasoning>
      <calculation>2/2 × 100 = 100</calculation>
    </analysis>
    <threshold_note>
      Score 100 requires:
      - Issue resolved during initial call (no callback/escalation)
      - Customer explicitly satisfied/thanked
      - Documentation completed
    </threshold_note>
  </example>

  <borderline_calibration score_A="50" score_B="100">
    <question>What separates FCR failure (50) from FCR success (100)?</question>
    <answer>
      - 100: Issue resolved on first call, no follow-up needed
      - 50: Escalation or callback required
      - 100: Customer satisfied/grateful
      - 50: Customer reluctant or disappointed
      - Gray area (70-80): Issue resolved but required 2+ attempts or partial satisfaction
    </answer>
  </borderline_calibration>

  <!-- Comprehensive Support Example -->

  <comprehensive_example>
    <call_summary>
      Complete support call showing empathy, diagnosis, and resolution
    </call_summary>
    <dialogue>
      <customer timestamp="00:05">Hi, I can't access my account. I've been trying for an hour!</customer>
      <agent timestamp="00:10">I'm so sorry to hear that, John. An hour is way too long. Let's fix this right now. What happens when you try to log in?</agent>
      <customer timestamp="00:18">It just says "error" and won't let me in</customer>
      <agent timestamp="00:22">OK, can you tell me the exact error message you see?</agent>
      <customer timestamp="00:28">"Session expired. Please log in again."</customer>
      <agent timestamp="00:32">Perfect, that's actually an easy fix. Let me clear your session on our end. Can you try now?</agent>
      <customer timestamp="00:40">[tries] Yes! It works!</customer>
      <agent timestamp="00:45">Excellent! This happens when you're logged in on multiple devices. To prevent it, please log out from old devices. If you have any issues in the next 24 hours, call us back anytime. Anything else I can help with?</agent>
      <customer timestamp="00:55">No, thank you so much!</customer>
    </dialogue>
    <comprehensive_analysis>
      <empathy>
        <score>85</score>
        <reasoning>Validated frustration, used name, showed urgency, warm tone</reasoning>
      </empathy>
      <problem_identification>
        <score>80</score>
        <reasoning>Asked for exact error message, diagnosed session issue correctly</reasoning>
      </problem_identification>
      <resolution_quality>
        <score>90</score>
        <reasoning>Fixed issue, verified, explained prevention, offered follow-up</reasoning>
      </resolution_quality>
      <first_call_resolution>
        <score>100</score>
        <reasoning>Resolved on first call, customer satisfied, documented</reasoning>
      </first_call_resolution>
      <overall_quality>88</overall_quality>
    </comprehensive_analysis>
  </comprehensive_example>

  <!-- Knowledge Base Usage -->

  <example category="knowledge_base_usage" score="40" tier="needs_improvement">
    <dialogue>
      <customer speaker="customer" timestamp="03:15">How do I export my data?</customer>
      <agent speaker="agent" timestamp="03:20">
        Uh, I'm not sure. Let me ask my supervisor.
      </agent>
    </dialogue>
    <analysis>
      <component name="knowledge_used">0 (didn't check KB)</component>
      <component name="self_sufficient">0 (escalated immediately)</component>
      <reasoning>Common question that should be in knowledge base. The agent did try to get help, so the score is 40 rather than lower.</reasoning>
    </analysis>
    <improvement>
      "Let me check our knowledge base for the exact steps. One moment..."
    </improvement>
  </example>

  <example category="knowledge_base_usage" score="90" tier="exceptional">
    <dialogue>
      <customer speaker="customer" timestamp="03:15">How do I export my data?</customer>
      <agent speaker="agent" timestamp="03:20">
        Great question! Go to Settings → Data → Export. You can choose CSV or JSON format. The export typically takes 2-3 minutes and you'll receive an email when it's ready. Would you like me to walk you through it?
      </agent>
    </dialogue>
    <analysis>
      <component name="knowledge_used">1 (precise instructions)</component>
      <component name="self_sufficient">1 (no escalation needed)</component>
      <reasoning>
        Comprehensive answer from KB.
        Offered additional help.
        Set expectations (2-3 minutes).
        Did not check that the export worked, so 90 rather than 100.
      </reasoning>
    </analysis>
  </example>

</examples>
`;
